import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  HttpLink,
  InMemoryCache,
  Observable,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { LocalStorageWrapper, CachePersistor } from "apollo3-cache-persist";
import { API_URL } from "config";
import { keycloakService } from "../keycloakService";
import { isInstalled } from "../pwa";
import { startInactiveSpan } from "../sentry";
import { persistenceMapper } from "./utils";

const mainLink = new HttpLink({
  uri: `${API_URL}/graphql`,
  fetchOptions: {
    mode: "cors",
  },
});

const authMiddleware = setContext(async (operation, { headers }) => {
  const token = await keycloakService.getToken();
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  };
});

const pickPortfolioComplexityAttrs = (
  variables: Record<string, unknown> | undefined
): Record<string, string | number> => {
  const attrs: Record<string, string | number> = {};
  if (!variables) return attrs;
  if (Array.isArray(variables.pfIds)) {
    attrs["graphql.variables.pf_ids_count"] = variables.pfIds.length;
  }
  if (Array.isArray(variables.portfolioIds)) {
    attrs["graphql.variables.portfolio_ids_count"] =
      variables.portfolioIds.length;
  }
  if (typeof variables.startDate === "string") {
    attrs["graphql.variables.start_date"] = variables.startDate;
  }
  return attrs;
};

const sentryTracingLink = new ApolloLink((operation, forward) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return forward(operation);

  const opName = operation.operationName || "anonymous";
  const span = startInactiveSpan({
    name: `gql ${opName}`,
    op: "graphql.operation",
    attributes: {
      "graphql.operation.name": opName,
      ...pickPortfolioComplexityAttrs(operation.variables),
    },
  });

  return new Observable<FetchResult>((observer) => {
    const subscription = forward(operation).subscribe({
      next: (value) => {
        if (value.errors?.length) {
          span?.setStatus({ code: 2, message: "graphql_errors" });
        }
        observer.next(value);
      },
      error: (err) => {
        span?.setStatus({ code: 2, message: "network_error" });
        span?.end();
        observer.error(err);
      },
      complete: () => {
        span?.end();
        observer.complete();
      },
    });
    return () => subscription.unsubscribe();
  });
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        portfolio: {
          read(_, { args, toReference }) {
            return toReference({
              __typename: "Portfolio",
              id: args?.id,
            });
          },
        },
      },
    },
    Portfolio: {
      fields: {
        portfolioReport: {
          merge: (existing, incoming) => {
            return { ...existing, ...incoming };
          },
        },
        portfolioGroups: {
          merge: (existing, incoming) => {
            return incoming;
          },
        },
      },
    },
    Contact: {
      fields: {
        portfolioReport: {
          merge: (existing, incoming) => {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    PortfolioReportItem: {
      keyFields: ["portfolioId", "security", ["id"]],
    },
    Document: {
      keyFields: ["identifier"],
    },
    Report: {
      keyFields: ["transactionId"],
    },
    TransactionType: {
      keyFields: ["typeCode"],
    },
  },
});

export const persistor = new CachePersistor({
  cache,
  storage: new LocalStorageWrapper(window.localStorage),
  maxSize: 20971520, // 20 MB
  persistenceMapper: persistenceMapper,
  key: "fa-client-cache-persist",
});

export const getPersistedApolloClient = async () => {
  await persistor.restore();

  if (!isInstalled) {
    persistor.pause();
  }

  return new ApolloClient({
    link: from([sentryTracingLink, authMiddleware, mainLink]),
    cache,
  });
};
